import { ErrorController, RestError } from "$lib/controller/error"
import { isProduction } from "$lib/environment"
import { getIsTastyWebView } from "$lib/util"
import * as Sentry from "@sentry/sveltekit"
import { applyHoneycombTraceToSentryEvent } from "@tastyworks/shared-web-telemetry/sentry-correlation"
import { get, writable } from "svelte/store"

// TODO: IS-915 - remove once native mobile app releases full cobrowse support
function maybeInitCobrowse() {
  if (!get(getIsTastyWebView())) {
    import("@tastyworks/shared-cobrowse/cobrowse").then(({ initCobrowse }) =>
      initCobrowse({ license: import.meta.env.VITE_COBROWSE_LICENSE_KEY })
    )
  }
}

maybeInitCobrowse()

// Browser OpenTelemetry tracing is initialised from the root +layout
// (see $lib/telemetry) once the LaunchDarkly `ao2-otel` flag can be read.

if (isProduction()) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: __APP_VERSION__,
    ignoreErrors: [
      // GTM noise, not impactful to user experience
      "Failed to fetch TypeError /gtag/js Mc Mc(gtag/js)",
      // Sentry's own framework wrapper error, expected per Sentry source:
      // "If you are a Sentry user, and you are seeing this stack frame, it
      // means the sentry.javascript SDK caught an error invoking your
      // application code. This is expected behavior and NOT indicative of a
      // bug with sentry.javascript."
      "Cookies is not defined",
      "Can't find variable: Cookies",
    ],
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["assert"],
      }),
    ],
    beforeSend(event, hint) {
      const { originalException } = hint
      if (originalException instanceof RestError) {
        event.message = ErrorController.getRestErrorMessage(originalException)
      }

      return applyHoneycombTraceToSentryEvent(event)
    },
  })
}

export const handleError = isProduction()
  ? Sentry.handleErrorWithSentry(ErrorController.handleLoadError)
  : ErrorController.handleLoadError

if (import.meta.vitest) {
  const { vi, describe, it, expect, beforeEach } = import.meta.vitest

  vi.mock("$app/navigation", () => ({
    goto: vi.fn(),
  }))

  vi.mock("@tastyworks/shared-cobrowse/cobrowse", () => ({
    initCobrowse: vi.fn(),
  }))

  vi.mock("$lib/util", () => ({
    getIsTastyWebView: vi.fn(),
  }))

  describe("maybeInitCobrowse", () => {
    let initCobrowseMock: ReturnType<typeof vi.fn>

    beforeEach(async () => {
      vi.clearAllMocks()
      const cobrowseModule =
        await import("@tastyworks/shared-cobrowse/cobrowse")
      initCobrowseMock = vi.mocked(cobrowseModule.initCobrowse)
    })

    it.each([
      [true, false],
      [false, true],
    ])(
      "isWebView=%s → initCobrowse called: %s",
      async (isWebView, shouldInit) => {
        const { getIsTastyWebView } = await import("$lib/util")
        vi.mocked(getIsTastyWebView).mockReturnValue(writable(isWebView))

        maybeInitCobrowse()
        await vi.waitFor(() => {
          if (shouldInit) {
            expect(initCobrowseMock).toHaveBeenCalledOnce()
          } else {
            expect(initCobrowseMock).not.toHaveBeenCalled()
          }
        })
      }
    )
  })
}
